import { SiFacebook } from "@icons-pack/react-simple-icons";
import { ArrowLeft } from "@phosphor-icons/react";
import { QueryClientProvider, useMutation } from "@tanstack/react-query";
import { actions, isInputError } from "astro:actions";
import { useMemo } from "react";

import google from "~/assets/brands/google.svg?url";
import microsoft from "~/assets/brands/microsoft.svg?url";
import {
  DialogHeader,
  type DialogHeaderLeftButton,
} from "~/components/DialogHeader";
import { useCreateQueryClient } from "~/hooks/query";

import { DialogBody } from "../DialogBody";
import { DialogTitle } from "../DialogTitle";
import { TextField } from "../TextField";
import { AuthButton } from "./AuthButton";
import { LoginForm } from "./LoginForm";
import { SignUpForm } from "./SignUpForm";

export const AuthForm: React.FC = () => {
  const queryClient = useCreateQueryClient();
  const { mutate, isPending, isSuccess, data, variables, error, reset } =
    useMutation({ mutationFn: actions.account.exists.orThrow }, queryClient);

  const backButton = useMemo<DialogHeaderLeftButton>(
    () => ({
      icon: ArrowLeft,
      onClick: reset,
    }),
    [reset],
  );

  if (isSuccess) {
    return (
      <QueryClientProvider client={queryClient}>
        {data ? (
          <LoginForm formData={variables}>
            <DialogHeader leftButton={backButton}>Log in</DialogHeader>
          </LoginForm>
        ) : (
          <SignUpForm formData={variables}>
            <DialogHeader leftButton={backButton}>
              Sign up for free
            </DialogHeader>
          </SignUpForm>
        )}
      </QueryClientProvider>
    );
  }

  return (
    <>
      <DialogHeader>Log in or sign up</DialogHeader>
      <DialogBody className="space-y-8">
        <form
          autoComplete="off"
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            mutate(formData);
          }}
        >
          <div className="space-y-4">
            <DialogTitle>Welcome to Knoword 👋</DialogTitle>
            <TextField
              required
              type="email"
              name="email"
              label="Continue with email"
              placeholder="Your email address"
              error={isInputError(error) ? error?.fields.email?.[0] : undefined}
            />
          </div>
          <button
            disabled={isPending}
            className="btn btn-secondary btn-block"
            type="submit"
          >
            Continue
          </button>
        </form>
        <div className="divider text-sm">or</div>
        <div className="space-y-3">
          <AuthButton provider="google">
            <img src={google} alt="Google logo" className="size-5" />
            Continue with Google
          </AuthButton>
          <AuthButton provider="microsoft">
            <img src={microsoft} alt="Microsoft logo" className="size-5" />
            Continue with Microsoft
          </AuthButton>
          <AuthButton provider="facebook">
            <SiFacebook className="size-5 fill-[#0866FF]" />
            Continue with Facebook
          </AuthButton>
        </div>
      </DialogBody>
    </>
  );
};
